import type { Change, ChangeOp, Item, LiveChange, Person, When } from "../../src/web/types.ts";
import { UNNAMED_AI } from "../../src/web/types.ts";
import type { IconName } from "../souvenir/index.js";
import { itemWhen, localWhen, paidMoney, STATUS, weekday } from "./format.ts";

// What a change did, one line per operation, in words: "Added Fushimi Inari Taisha", "Changed Gion:
// when, notes". Lines about an item that's still in the trip carry its id, to open it on the map.

export interface ChangeLine {
  icon: IconName;
  text: string;
  item?: string;
}

interface Context {
  items: Map<string, Item>;
  members: Person[];
  you: string;
}

const FIELD: Record<string, string> = {
  title: "name",
  when: "when",
  where: "where",
  from: "from",
  to: "to",
  notes: "notes",
  cost: "cost",
  paid: "who paid",
  charges: "charged later",
  refunds: "refunds",
  booking: "booking",
  booking_needed: "booking needed",
  books_from: "when booking opens",
  status: "status",
  category: "category",
  parent: "choice",
  best_time: "best time",
  duration: "duration",
  pros: "pros",
  cons: "cons",
  links: "links",
  origin: "where it came from",
  recommended_by: "who recommends it",
  owner: "who it's for",
  due: "due date",
  important: "important",
  brief: "brief",
  dates: "dates",
  budget: "budget",
};

const str = (v: unknown) => (typeof v === "string" ? v : undefined);
// A thing by its kind, where its name isn't known: one written blank before 2026-10-30, taken off since.
const A_KIND: Record<string, string> = {
  place: "a place",
  goal: "a goal",
  choice: "a choice",
  stay: "a stay",
  move: "a journey",
  task: "a to-do",
  info: "some info",
};
// A name as it was written, or nothing for one written blank (none is since 2026-10-30: DESIGN.md, Records, "A name is
// never blank"), so a line from before says the name as it is now.
const named = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);

// A rating as it was said: one of the three, or, in a change from before, "must" (until 2026-10-23), "want"
// or "if there's time" (until 2026-10-21) (src/tiers.ts).
const SAID: Record<string, string> = {
  yes: "yes",
  maybe: "maybe",
  no: "no",
  must: "must",
  want: "want",
  if_time: "if there's time",
};
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// The top-level fields an update changed, in words.
function changedFields(before: Record<string, unknown> = {}, after: Record<string, unknown> = {}, skip: string[] = []) {
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return keys
    .filter((k) => !skip.includes(k) && !same(before[k], after[k]))
    .map((k) => FIELD[k] ?? k.replace(/_/g, " "));
}

function host(url: unknown): string {
  try {
    return new URL(String(url)).hostname.replace(/^www\./, "");
  } catch {
    return "a link";
  }
}

// "address", "address and note", "address, note and what it's about".
function listed(words: string[]): string {
  return words.length < 2 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

// A card's own links (its website, email and social pages, its booking page, its other links), each address once.
function ownLinks(fields: Record<string, unknown>): string[] {
  const contact = (fields["contact"] ?? {}) as Record<string, unknown>;
  const booking = (fields["booking"] ?? {}) as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((u): u is string => typeof u === "string") : []);
  const email = str(contact["email"]);
  return [
    str(contact["website"]),
    email && `mailto:${email}`,
    str(booking["url"]),
    ...list(fields["links"]),
    ...list(contact["social"]),
  ].filter((u): u is string => Boolean(u));
}

// What's left of a card's fields without its links: their change alone is a link's.
function withoutLinks(fields: Record<string, unknown>): Record<string, unknown> {
  const { links: _links, contact, booking, ...rest } = fields;
  const { website: _w, email: _e, social: _s, ...otherContact } = (contact ?? {}) as Record<string, unknown>;
  const { url: _u, ...otherBooking } = (booking ?? {}) as Record<string, unknown>;
  return { ...rest, contact: otherContact, booking: otherBooking };
}

// A card's own link changed or taken off, alone, as the web does it (CardLinks.tsx): "Hotel Granvia Kyoto: a link
// changed to granvia-kyoto.co.jp", "Hotel Granvia Kyoto: a link taken off, instagram.com". Null for anything more.
function ownLinkLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): { icon: IconName; text: string } | null {
  if (!same(withoutLinks(before), withoutLinks(after))) return null;
  const [was, now] = [ownLinks(before), ownLinks(after)];
  const gone = was.filter((u) => !now.includes(u));
  const added = now.filter((u) => !was.includes(u));
  const said = (u: string) => (u.startsWith("mailto:") ? u.slice(7) : host(u));
  if (gone.length === 0 && added.length === 0) return null;
  if (added.length === 0) return { icon: "close", text: `${title}: a link taken off, ${gone.map(said).join(", ")}` };
  if (gone.length === 0) return { icon: "share", text: `${title}: a link added, ${added.map(said).join(", ")}` };
  return { icon: "share", text: `${title}: a link changed to ${added.map(said).join(", ")}` };
}

function clip(text: string, n = 120): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > n ? `${flat.slice(0, n - 1)}…` : flat;
}

export function changeLines(change: Change, ctx: Context): ChangeLine[] {
  const nameOf = (person: unknown) => {
    if (person === ctx.you) return "you";
    return ctx.members.find((m) => m.id === person)?.name ?? "someone";
  };
  const live = (id: string | undefined) => (id && ctx.items.has(id) ? { item: id } : {});
  const itemTitle = (o: ChangeOp) =>
    named(o.after?.["title"]) ??
    named(o.before?.["title"]) ??
    named(ctx.items.get(o.item ?? o.id)?.title) ??
    A_KIND[str(o.after?.["type"]) ?? str(o.before?.["type"]) ?? "place"] ??
    "a place";

  return change.ops.map((o): ChangeLine => {
    switch (o.record) {
      case "item": {
        const title = itemTitle(o);
        if (o.op === "create") return { icon: "plus", text: `Added ${title}`, ...live(o.id) };
        if (o.op === "delete") return { icon: "close", text: `Removed ${title}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back ${title}`, ...live(o.id) };
        const fields = changedFields(o.before ?? {}, o.after);
        const status = str(o.after?.["status"]);
        // Given a name where it had none (Tripmo's change, 2026-10-30): "Named Kiyomizu-dera".
        if (fields.length === 1 && fields[0] === FIELD["title"] && o.before && !named(o.before["title"]))
          return { icon: "note", text: `Named ${title}`, ...live(o.id) };
        // A to-do changed one way, as the web changes them: said the way people say it.
        if (str(o.after?.["type"]) === "task" && fields.length === 1) {
          const todo = todoLine(title, o.before ?? {}, o.after ?? {}, nameOf);
          if (todo) return { ...todo, ...live(o.id) };
        }
        // Only a link of its own changed, as the web changes them: "Hotel Granvia Kyoto: a link changed to …".
        const link = ownLinkLine(title, o.before ?? {}, o.after ?? {});
        if (link) return { ...link, ...live(o.id) };
        // Only its city changed, as the web changes it: "Hotel Granvia Kyoto: in Kyoto".
        const city = cityLine(title, o.before ?? {}, o.after ?? {});
        if (city) return { ...city, ...live(o.id) };
        // A place or goal put on a day, moved or taken off it (Plan for a day, Today, an AI's sketch): "Nishiki
        // Market: on Tue 8 Dec · morning", "Nishiki Market: off its day".
        const day = dayLine(title, o.before ?? {}, o.after ?? {});
        if (day) return { ...day, ...live(o.id) };
        // A payment added or taken off, as Add a payment does: "Dinner at Gion Karyo: ¥6,000 paid by you · Cash".
        const pay = paymentLine(title, o.before ?? {}, o.after ?? {}, nameOf);
        if (pay) return { ...pay, ...live(o.id) };
        // A charge later added, taken off, or confirmed as paid: "Mimatsukan: SGD 600 charged to Mastercard 8682 on
        // Sun 1 Nov", "Mimatsukan: SGD 600 charged on Sun 1 Nov, paid by you · Mastercard 8682".
        const charge = chargeLine(title, o.before ?? {}, o.after ?? {}, nameOf);
        if (charge) return { ...charge, ...live(o.id) };
        // Called off: off the map, so nothing to open.
        if (status === "cancelled" && o.before?.["status"] !== "cancelled")
          return { icon: "close", text: `Cancelled ${title}` };
        // Booked (the web's booking form, or an AI): said so, then what else came with it ("Booked Cross Hotel
        // Osaka: when, cost, who paid, booking").
        if (status === "booked" && o.before?.["status"] !== "booked") {
          const rest = fields.filter((f) => f !== "status");
          return { icon: "lock", text: `Booked ${title}${rest.length ? `: ${rest.join(", ")}` : ""}`, ...live(o.id) };
        }
        // A booking marked by mistake, put back to needing booking (the booking form's Not booked after all), and a
        // Not this time taken back (Today's Back in the plan): said the way the web says them.
        const was = str(o.before?.["status"]);
        if (was === "booked" && (status === "planned" || status === "idea" || status === "option"))
          return { icon: "replay", text: `${title}: not booked after all`, ...live(o.id) };
        if (
          was === "missed" &&
          fields.length === 1 &&
          (status === "idea" || status === "planned" || status === "booked")
        )
          return { icon: "replay", text: `${title}: back in the plan`, ...live(o.id) };
        if (fields.length === 1 && fields[0] === "status" && status)
          return { icon: "check", text: `${title}: ${STATUS[status]?.label.toLowerCase() ?? status}`, ...live(o.id) };
        return {
          icon: "note",
          text: fields.length ? `Changed ${title}: ${fields.join(", ")}` : `Changed ${title}`,
          ...live(o.id),
        };
      }
      case "interest": {
        const title = itemTitle(o);
        if (o.op === "delete" || (o.op === "update" && !o.after)) return { icon: "heart", text: `Unrated ${title}` };
        if (o.op === "restore") return { icon: "heart", text: `Rated ${title} again`, ...live(o.item) };
        const tier = str(o.after?.["tier"]);
        const going = str(o.after?.["going"]);
        // Tripmo's own change to many people's ratings (three tiers, then Yes): whose each was, and what it
        // said before.
        const was = str(o.before?.["tier"]);
        if (o.person && tier && was)
          return {
            icon: "heart",
            text: `${title}: ${SAID[tier] ?? tier} for ${nameOf(o.person)} (was ${SAID[was] ?? was})`,
            ...live(o.item),
          };
        const parts = [
          tier ? (SAID[tier] ?? tier) : null,
          going === "yes" ? "going" : going === "no" ? "not going" : going === "maybe" ? "maybe going" : null,
        ].filter(Boolean);
        return { icon: "heart", text: `${title}: ${parts.join(", ") || "no rating"}`, ...live(o.item) };
      }
      case "link": {
        const about = str(o.after?.["note"]) ?? host(o.after?.["url"] ?? o.before?.["url"]);
        const items = (v: unknown) => (Array.isArray(v) ? v.filter((i): i is string => typeof i === "string") : []);
        // What it's about, by the first's title ("Gogyo Ramen and 2 more"), opening it while it's in the trip.
        const on = (ids: string[]) => {
          const titles = ids.flatMap((id) => ctx.items.get(id)?.title ?? []);
          return titles.length === 0 ? "" : `${titles[0]}${titles.length > 1 ? ` and ${titles.length - 1} more` : ""}`;
        };
        const now = items(o.after?.["items"]);
        if (o.op === "create") {
          const subject = on(now);
          return subject
            ? { icon: "share", text: `Saved a link about ${subject}: ${about}`, ...live(now[0]) }
            : { icon: "share", text: `Saved a link: ${about}` };
        }
        if (o.op === "delete") return { icon: "close", text: `Removed a link: ${host(o.before?.["url"])}` };
        if (o.op === "restore") return { icon: "replay", text: "Brought back a link" };
        // Said the way the web changes them (features/links.md, "Add, edit and remove every link on the web"): what
        // it's about alone, "Put a link on Gogyo Ramen: …" (or back in the inbox); else what changed of it.
        const was = items(o.before?.["items"]);
        const moved = !same(was, now);
        const fields = [
          !same(o.before?.["url"], o.after?.["url"]) && "address",
          !same(o.before?.["note"], o.after?.["note"]) && "note",
          moved && "what it's about",
        ].filter((f): f is string => Boolean(f));
        if (moved && fields.length === 1)
          return now.length === 0
            ? { icon: "share", text: `Put a link back in the inbox: ${about}` }
            : { icon: "share", text: `Put a link on ${on(now) || "a place"}: ${about}`, ...live(now[0]) };
        return {
          icon: "share",
          text: fields.length > 0 ? `Changed a link's ${listed(fields)}: ${about}` : `Changed a link: ${about}`,
        };
      }
      case "file": {
        const on = o.on ?? "";
        const target = on.startsWith("doc_") ? "a travel document" : (ctx.items.get(on)?.title ?? "a place");
        const name = str(o.after?.["name"]) ?? str(o.before?.["name"]) ?? "a file";
        const open = on.startsWith("itm_") ? live(on) : {};
        if ((o.after?.["as"] ?? o.before?.["as"]) === "photo") {
          if (o.op === "create") return { icon: "camera", text: `Added a photo of ${target}`, ...open };
          if (o.op === "delete") return { icon: "close", text: `Removed a photo of ${target}` };
          return { icon: "replay", text: `Brought back a photo of ${target}`, ...open };
        }
        if (o.op === "create") return { icon: "plus", text: `Attached ${name} to ${target}`, ...open };
        if (o.op === "delete") return { icon: "close", text: `Removed ${name} from ${target}` };
        return { icon: "replay", text: `Brought back a file on ${target}`, ...open };
      }
      case "research": {
        const title = str(o.after?.["title"]) ?? str(o.before?.["title"]) ?? "research";
        if (o.op === "create") return { icon: "book", text: `Found out: ${title}` };
        if (o.op === "delete") return { icon: "close", text: `Removed research: ${title}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back research: ${title}` };
        return { icon: "book", text: `Added to research: ${title}` };
      }
      case "pad": {
        const name = named(o.after?.["name"]) ?? named(o.before?.["name"]) ?? "a pad";
        if (o.op === "create")
          return { icon: "meet", text: `Made ${name} for ${named(o.after?.["for"]) ?? "a friend"}` };
        if (o.op === "delete") return { icon: "close", text: `Removed ${name}` };
        if (o.op === "restore") return { icon: "replay", text: "Brought back a pad" };
        if (o.after?.["link"]) return { icon: "meet", text: `Replaced the link to ${name}` };
        if (o.before?.["closed"] !== o.after?.["closed"])
          return { icon: "meet", text: o.after?.["closed"] ? `Closed ${name}` : `Reopened ${name}` };
        // Given a name where it had none (Tripmo's change, 2026-10-30): "Named Jon's recommendations".
        if (o.before && !named(o.before["name"])) return { icon: "meet", text: `Named ${name}` };
        return {
          icon: "meet",
          text: `Changed ${name}: ${changedFields(o.before ?? {}, o.after, ["closed"]).join(", ")}`,
        };
      }
      case "note": {
        const on = o.on ?? "";
        const target = on.startsWith("trp_")
          ? "the trip"
          : on.startsWith("mem_")
            ? (ctx.members.find((m) => m.member === on)?.name ?? "someone")
            : on.startsWith("rsr_")
              ? "research"
              : (ctx.items.get(on)?.title ?? "a place");
        const words = str(o.after?.["text"]) ?? str(o.before?.["text"]);
        const quote = words ? `: “${clip(words, 80)}”` : "";
        const open = on.startsWith("itm_") ? live(on) : {};
        if (o.op === "create") return { icon: "note", text: `Noted on ${target}${quote}`, ...open };
        if (o.op === "delete") return { icon: "close", text: `Removed a note on ${target}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back a note on ${target}`, ...open };
        return { icon: "note", text: `Changed a note on ${target}${quote}`, ...open };
      }
      case "ask": {
        const text = str(o.after?.["text"]) ?? str(o.before?.["text"]) ?? "a question";
        const quote = `“${clip(text, 80)}”`;
        const about = live(o.about);
        if (o.op === "create") return { icon: "bell", text: `Asked ${quote}`, ...about };
        if (o.op === "delete") return { icon: "close", text: `Removed the question ${quote}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back the question ${quote}`, ...about };
        if (o.before?.["status"] !== o.after?.["status"])
          return {
            icon: "bell",
            text: `${o.after?.["status"] === "closed" ? "Closed" : "Reopened"} ${quote}`,
            ...about,
          };
        return { icon: "bell", text: `Changed the question ${quote}`, ...about };
      }
      case "reminder": {
        const text = str(o.after?.["text"]) ?? str(o.before?.["text"]) ?? "a reminder";
        const quote = `“${clip(text, 80)}”`;
        const about = live(o.about);
        if (o.op === "create") return { icon: "bell", text: `Set a reminder: ${quote}`, ...about };
        if (o.op === "delete") return { icon: "close", text: `Removed the reminder ${quote}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back the reminder ${quote}`, ...about };
        // What changed, as the web changes it (features/tasks-and-reminders.md, "Reminders on the web"): its words,
        // its time, who it's for; one alone said in full, several named.
        const [was, now] = [o.before ?? {}, o.after ?? {}];
        const reworded = !same(was["text"], now["text"]);
        const moved = !same(was["at"], now["at"]) || !same(was["tz"], now["tz"]);
        const readdressed = !same(was["to"], now["to"]);
        const at = str(now["at"]);
        const tz = str(now["tz"]);
        const whom = (to: unknown) =>
          Array.isArray(to) && to.length > 0 ? listed(to.map(nameOf)) : "everyone in the trip";
        const changed = [reworded && "its words", moved && "when", readdressed && "who it's for"].filter(
          (w): w is string => Boolean(w),
        );
        if (changed.length === 1 && reworded) return { icon: "bell", text: `Reworded a reminder: ${quote}`, ...about };
        if (changed.length === 1 && moved && at && tz)
          return { icon: "bell", text: `Moved the reminder ${quote} to ${localWhen(at, tz)}`, ...about };
        if (changed.length === 1 && readdressed)
          return { icon: "bell", text: `The reminder ${quote} now goes to ${whom(now["to"])}`, ...about };
        if (changed.length > 1)
          return { icon: "bell", text: `Changed the reminder ${quote}: ${listed(changed)}`, ...about };
        return { icon: "bell", text: `Changed the reminder ${quote}`, ...about };
      }
      case "document": {
        const title = str(o.after?.["title"]) ?? str(o.before?.["title"]) ?? "a document";
        if (o.op === "create") return { icon: "book", text: `Added ${title}` };
        if (o.op === "delete") return { icon: "close", text: `Removed ${title}` };
        if (o.op === "restore") return { icon: "replay", text: `Brought back ${title}` };
        // What changed, by name only: a document's details aren't for the feed.
        const fields = changedFields(o.before ?? {}, o.after, ["details_hidden"]).map((f) =>
          f === "shared with" || f === "shared_with" ? "who sees the details" : f,
        );
        return { icon: "book", text: fields.length ? `Changed ${title}: ${fields.join(", ")}` : `Changed ${title}` };
      }
      case "answer": {
        if (o.op === "delete") return { icon: "close", text: "Took back an answer" };
        if (o.op === "restore") return { icon: "replay", text: "Brought back an answer" };
        const said = [str(o.after?.["choice"]), str(o.after?.["text"]) && `“${clip(str(o.after?.["text"]) ?? "", 80)}”`]
          .filter(Boolean)
          .join(": ");
        return { icon: "bell", text: `Answered${said ? ` ${said}` : ""}` };
      }
      case "pad_entry":
        if (o.op === "create") return { icon: "meet", text: `“${clip(str(o.after?.["text"]) ?? "")}”` };
        // The friend fixing or removing their own tip, on their page (features/recommendations.md).
        if (o.op === "delete")
          return { icon: "close", text: `Removed a tip: “${clip(str(o.before?.["text"]) ?? "")}”` };
        if (o.after && "text" in o.after)
          return { icon: "meet", text: `Changed a tip: “${clip(str(o.after["text"]) ?? "")}”` };
        return {
          icon: "check",
          text: o.after?.["taken"] ? "Took a friend's tip into the plan" : "Put a friend's tip back",
        };
      case "trip": {
        if (o.op === "restore") return { icon: "replay", text: "Restored the trip" };
        if (o.op !== "update")
          return { icon: "calendar", text: o.op === "delete" ? "Deleted the trip" : "Made the trip" };
        if (!same(o.before?.["owner"], o.after?.["owner"]))
          // The member it was offered to accepting it (DESIGN.md, Member, "Taking over a trip"), else an owner's
          // account deleted, which hands it on.
          return change.by && "id" in change.by && change.by.id === o.after?.["owner"]
            ? { icon: "people", text: "Took over the trip" }
            : { icon: "people", text: `Handed the trip to ${nameOf(o.after?.["owner"])}` };
        if (o.after?.["join_link"]) return { icon: "people", text: "Made a new join link" };
        const fields = changedFields(o.before ?? {}, o.after, ["owner"]);
        // Given a name where it had none (Tripmo's change, 2026-10-30): "Named the trip: Untitled trip".
        if (fields.length === 1 && fields[0] === FIELD["title"] && o.before && !named(o.before["title"]))
          return { icon: "calendar", text: `Named the trip: ${named(o.after?.["title"]) ?? "a trip"}` };
        return {
          icon: "calendar",
          text: `Changed the trip: ${fields.join(", ")}`,
        };
      }
      // The owner offering the trip to a member, taking the offer back, or the member turning it down.
      case "offer": {
        const to = o.after?.["to"];
        const was = o.before?.["to"];
        if (o.op === "create") return { icon: "people", text: `Offered the trip to ${nameOf(to)}` };
        if (o.op === "update")
          return {
            icon: "people",
            text: same(to, was)
              ? `Offered the trip to ${nameOf(to)} again`
              : `Offered the trip to ${nameOf(to)} instead of ${nameOf(was)}`,
          };
        return change.by && "id" in change.by && change.by.id === was
          ? { icon: "people", text: "Turned down taking over the trip" }
          : { icon: "people", text: `Took back the offer of the trip to ${nameOf(was)}` };
      }
      case "member": {
        const person = o.after?.["person"] ?? o.before?.["person"];
        const self = change.by && "id" in change.by && change.by.id === person;
        if (o.op === "create") return { icon: "people", text: "Asked to join" };
        if (o.op === "delete") {
          if (self)
            return {
              icon: "people",
              text: o.before?.["status"] === "requested" ? "Took back their request to join" : "Left the trip",
            };
          return {
            icon: "people",
            text: o.before?.["status"] === "requested" ? `Turned down ${nameOf(person)}` : `Removed ${nameOf(person)}`,
          };
        }
        // Their account was deleted (features/privacy-and-data.md): they stay, as a former member.
        if (o.after?.["status"] === "former")
          return { icon: "people", text: "Deleted their Tripmo account: what they added stays" };
        if (o.before?.["status"] !== o.after?.["status"]) return { icon: "people", text: `Let ${nameOf(person)} in` };
        if (Boolean(o.before?.["sharing_location"]) !== Boolean(o.after?.["sharing_location"]))
          return {
            icon: "pin",
            text: o.after?.["sharing_location"] ? "Started sharing their location" : "Stopped sharing their location",
          };
        if (self)
          return { icon: "people", text: person === ctx.you ? "Updated your profile" : "Updated their profile" };
        return { icon: "people", text: `Updated ${nameOf(person)}'s profile` };
      }
      default:
        return { icon: "note", text: "Changed something" };
    }
  });
}

const DUE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

// One thing changed on a to-do: "Gave Buy JR passes to Ben", "Buy JR passes: due 18 Nov".
// A change to where something is that changed only its city: said as people say it.
function cityLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Omit<ChangeLine, "item"> | null {
  if (changedFields(before, after).join() !== "where") return null;
  const [was, now] = [before["where"], after["where"]].map((w) =>
    w && typeof w === "object" ? (w as Record<string, unknown>) : {},
  ) as [Record<string, unknown>, Record<string, unknown>];
  const { city: a, ...restBefore } = was;
  const { city: b, ...restAfter } = now;
  if (same(a, b) || !same(restBefore, restAfter)) return null;
  const city = str(b);
  return { icon: "pin", text: city ? `${title}: in ${city}` : `${title}: no city` };
}

function dayLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Omit<ChangeLine, "item"> | null {
  const type = str(after["type"]);
  if (type !== "place" && type !== "goal") return null;
  const fields = changedFields(before, after);
  // Its when, and nothing else but, as it's placed, being in the plan now.
  if (!fields.includes("when") || fields.some((f) => f !== "when" && f !== "status")) return null;
  if (fields.includes("status") && after["status"] !== "planned") return null;
  const now = itemWhen(after["when"] as When | undefined);
  const placed = now !== null && !/^Any /.test(now);
  return { icon: "calendar", text: placed ? `${title}: on ${now}` : `${title}: off its day` };
}

type Paid = { by?: unknown; amount?: unknown; currency?: unknown; method?: unknown };
function paymentLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  nameOf: (person: unknown) => string,
): Omit<ChangeLine, "item"> | null {
  if (changedFields(before, after).join() !== "who paid") return null;
  const was = (Array.isArray(before["paid"]) ? before["paid"] : []) as Paid[];
  const now = (Array.isArray(after["paid"]) ? after["paid"] : []) as Paid[];
  const [longer, shorter] = now.length > was.length ? [now, was] : [was, now];
  if (longer.length !== shorter.length + 1) return null;
  // The one payment that's in one and not the other, the rest in the same order.
  const at = longer.findIndex((p, i) => !same(p, shorter[i]));
  const rest = longer.filter((_, i) => i !== at);
  if (at < 0 || !same(rest, shorter)) return null;
  const p = longer[at] as Paid;
  const money =
    typeof p.amount === "number" && typeof p.currency === "string" ? paidMoney(p.amount, p.currency) : "A payment";
  const said = `${money} paid by ${nameOf(p.by)}${typeof p.method === "string" ? ` · ${p.method}` : ""}`;
  return now.length > was.length
    ? { icon: "check", text: `${title}: ${said}` }
    : { icon: "close", text: `${title}: ${said}, taken off` };
}

type Charged = Paid & { on?: unknown };
const listOf = <T>(v: unknown) => (Array.isArray(v) ? v : []) as T[];
// The one entry in `longer` that isn't in `shorter` (the rest in the same order), or none.
function oneMore<T>(longer: T[], shorter: T[]): T | null {
  if (longer.length !== shorter.length + 1) return null;
  const at = longer.findIndex((p, i) => !same(p, shorter[i]));
  return at >= 0 &&
    same(
      longer.filter((_, i) => i !== at),
      shorter,
    )
    ? (longer[at] as T)
    : null;
}

function chargeLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  nameOf: (person: unknown) => string,
): Omit<ChangeLine, "item"> | null {
  const fields = changedFields(before, after).sort().join();
  const was = listOf<Charged>(before["charges"]);
  const now = listOf<Charged>(after["charges"]);
  const money = (c: Charged) =>
    typeof c.amount === "number" && typeof c.currency === "string" ? paidMoney(c.amount, c.currency) : "A charge";
  const day = (c: Charged) => (typeof c.on === "string" ? ` on ${weekday(c.on)}` : "");
  const card = (c: Charged) => {
    if (typeof c.method === "string") return c.method;
    const whose = nameOf(c.by);
    return whose === "you" ? "your card" : `${whose}'s card`;
  };
  if (fields === "charged later") {
    const added = oneMore(now, was);
    if (added) return { icon: "clock", text: `${title}: ${money(added)} charged to ${card(added)}${day(added)}` };
    const gone = oneMore(was, now);
    if (gone)
      return { icon: "close", text: `${title}: ${money(gone)} charged to ${card(gone)}${day(gone)}, taken off` };
    return null;
  }
  if (fields === "charged later,who paid") {
    const gone = oneMore(was, now);
    const paid = oneMore(listOf<Paid>(after["paid"]), listOf<Paid>(before["paid"]));
    if (!gone || !paid) return null;
    const { on: _, ...asPaid } = gone;
    if (!same(asPaid, paid)) return null;
    const how = typeof paid.method === "string" ? ` · ${paid.method}` : "";
    return { icon: "check", text: `${title}: ${money(gone)} charged${day(gone)}, paid by ${nameOf(paid.by)}${how}` };
  }
  return null;
}

function todoLine(
  title: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  nameOf: (person: unknown) => string,
): Omit<ChangeLine, "item"> | null {
  if (!same(before["status"], after["status"]))
    return after["status"] === "done"
      ? { icon: "check", text: `${title}: done` }
      : { icon: "replay", text: `${title}: not done` };
  if (!same(before["owner"], after["owner"]))
    return after["owner"]
      ? { icon: "people", text: `Gave ${title} to ${nameOf(after["owner"])}` }
      : { icon: "people", text: `${title}: for nobody yet` };
  if (!same(before["due"], after["due"])) {
    const due = str(after["due"]);
    return {
      icon: "calendar",
      text: due ? `${title}: due ${DUE.format(new Date(`${due}T00:00:00Z`))}` : `${title}: no due date`,
    };
  }
  if (!same(before["important"], after["important"]))
    return { icon: "bell", text: after["important"] ? `${title}: important` : `${title}: not important` };
  return null;
}

// The live-update banner: // "Mei changed 3 things", "Your Claude and Ben changed 7 things", "Jon wrote 2 tips".
export function liveText(changes: LiveChange[], members: Person[], you: string): string {
  const names: string[] = [];
  for (const c of changes) {
    const name =
      "friend" in c.by
        ? c.by.friend
        : c.by.id === you
          ? c.via && c.via !== "web"
            ? c.via === UNNAMED_AI
              ? "your AI"
              : `your ${c.via}`
            : "you"
          : (members.find((m) => m.id === (c.by as { id: string }).id)?.name ?? c.by.name);
    if (!names.includes(name)) names.push(name);
  }
  const n = changes.reduce((sum, c) => sum + c.ops, 0);
  const who =
    names.length <= 3
      ? names.length === 1
        ? (names[0] as string)
        : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`
      : `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
  // A friend fixing or removing a tip says so as a new one does (features/recommendations.md).
  const text = changes.every((c) => "friend" in c.by)
    ? `${who} ${changes.every((c) => c.wrote !== false) ? "wrote" : "changed"} ${n} ${n === 1 ? "tip" : "tips"}`
    : `${who} changed ${n} ${n === 1 ? "thing" : "things"}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
